import { motion } from "framer-motion";
import { Link } from "react-router-dom";

const Hero = () => {
  return (
    <section className="min-h-screen bg-white flex flex-col justify-between relative">
      {/* Background dot pattern */}
      <div className="fixed inset-0 opacity-[0.03]" aria-hidden="true">
        <div
          className="h-full w-full"
          style={{
            backgroundImage: `radial-gradient(circle, #000 1px, transparent 1px)`,
            backgroundSize: "32px 32px",
          }}
        />
      </div>

      <div className="relative flex-1 flex flex-col">
        {/* Top bar */}
        <motion.header
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="desktop:px-24 laptop:px-20 tablet:px-12 mobile:px-6 h-24 flex items-center justify-between border-b border-neutral-100"
        >
          <Link to="/" className="font-medium tracking-tight text-lg">
            TEST®
          </Link>
          <nav className="desktop:flex laptop:flex tablet:hidden mobile:hidden gap-16 text-sm tracking-wide">
            <Link
              to="#"
              className="text-neutral-500 hover:text-black transition-colors"
            >
              Projects
            </Link>
            <Link
              to="#"
              className="text-neutral-500 hover:text-black transition-colors"
            >
              Studio
            </Link>
            <Link
              to="#"
              className="text-neutral-500 hover:text-black transition-colors"
            >
              Contact
            </Link>
          </nav>
        </motion.header>

        {/* Main content */}
        <div className="flex-1 desktop:px-24 laptop:px-20 tablet:px-12 mobile:px-6">
          <div className="h-full flex items-center">
            <div className="w-full max-w-[1600px] mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="desktop:mb-16 laptop:mb-16 tablet:mb-12 mobile:mb-8"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-[1px] bg-black"></div>
                  <span className="text-xs tracking-[0.3em] uppercase">
                    2024
                  </span>
                </div>
              </motion.div>

              <div className="grid desktop:grid-cols-[2fr,1fr] laptop:grid-cols-[2fr,1fr] tablet:grid-cols-1 mobile:grid-cols-1 gap-24">
                <div>
                  <motion.h1
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, delay: 0.2 }}
                    className="desktop:text-[180px] laptop:text-[140px] tablet:text-[100px] mobile:text-[64px]
                             font-bold tracking-[-0.04em] leading-[0.85] mb-12"
                  >
                    test
                  </motion.h1>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.4 }}
                    className="desktop:text-2xl laptop:text-2xl tablet:text-xl mobile:text-lg 
                             font-light leading-relaxed text-neutral-600 mb-16"
                  >
                    test — minimalist black and white design, sharp typography,
                    no images
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.6 }}
                    className="flex desktop:flex-row laptop:flex-row tablet:flex-row mobile:flex-col gap-6"
                  >
                    <Link
                      to="#"
                      className="group relative inline-flex items-center
                               desktop:px-12 laptop:px-12 tablet:px-10 mobile:px-8
                               h-16 bg-black text-white
                               text-sm tracking-[0.2em] uppercase
                               transition-all duration-300
                               hover:bg-neutral-900"
                    >
                      Explore
                      <svg
                        className="ml-4 w-4 h-4 transition-transform group-hover:translate-x-1"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M17 8l4 4m0 0l-4 4m4-4H3" />
                      </svg>
                    </Link>
                  </motion.div>
                </div>

                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, delay: 0.8 }}
                  className="desktop:block laptop:block tablet:hidden mobile:hidden"
                >
                  <div className="sticky top-24 pt-24">
                    <div className="flex flex-col gap-8 text-sm">
                      <div>
                        <div className="text-neutral-400 mb-2 tracking-wider uppercase">
                          Location
                        </div>
                        <div>New York, NY</div>
                      </div>
                      <div>
                        <div className="text-neutral-400 mb-2 tracking-wider uppercase">
                          Services
                        </div>
                        <div>
                          Design Direction
                          <br />
                          Brand Identity
                          <br />
                          Digital Experience
                        </div>
                      </div>
                      <div>
                        <div className="text-neutral-400 mb-2 tracking-wider uppercase">
                          Contact
                        </div>
                        <Link
                          to="mailto:hello@test.com"
                          className="hover:text-neutral-500 transition-colors"
                        >
                          hello@test.com
                        </Link>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <motion.footer
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="desktop:px-24 laptop:px-20 tablet:px-12 mobile:px-6 
                     h-24 border-t border-neutral-100
                     flex justify-between items-center"
        >
          <div className="flex items-center gap-12">
            <span className="text-xs tracking-wider text-neutral-400">
              © 2024
            </span>
            <div className="desktop:flex laptop:flex tablet:flex mobile:hidden gap-12">
              <Link
                to="#"
                className="text-xs tracking-wider text-neutral-400 hover:text-black transition-colors"
              >
                Instagram
              </Link>
              <Link
                to="#"
                className="text-xs tracking-wider text-neutral-400 hover:text-black transition-colors"
              >
                Twitter
              </Link>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs tracking-wider text-neutral-400">
              Scroll
            </span>
            <svg
              className="w-4 h-4 text-neutral-400 animate-bounce"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
          </div>
        </motion.footer>
      </div>
    </section>
  );
};

export default Hero;
